import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

export default function DashboardNav() {
  const { profile, user } = useAuth();
  const [menuOpen,     setMenuOpen    ] = useState(false);
  const [hasContacts,  setHasContacts ] = useState(false);
  const navigate   = useNavigate();
  const { pathname } = useLocation();

  // Gate Wrapped link: only show if user has at least one contact
  useEffect(() => {
    if (!user) return;
    import('../../lib/api').then(({ apiFetch }) => {
      apiFetch<{ contacts: unknown[] }>('/api/contacts?limit=1')
        .then(json => setHasContacts((json.contacts?.length ?? 0) > 0))
        .catch(() => {});
    });
  }, [user]);

  const displayName = profile?.first_name
    || user?.email?.split('@')[0]
    || 'You';

  const initials = displayName
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  async function handleSignOut() {
    const { supabase } = await import('../../lib/supabase');
    await supabase.auth.signOut();
    navigate('/');
  }

  return (
  <>
    <nav className="sticky top-0 z-30 bg-cream/95 backdrop-blur-sm border-b border-gray-100">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 flex items-center h-16 gap-6">

        {/* Logo — M+S monogram + wordmark text */}
        <Link to="/dashboard" className="flex items-center gap-2 flex-shrink-0">
          <img
            src="/logo-icon.png"
            alt="MomentSent"
            className="h-8 w-auto object-contain"
          />
          <span className="font-heading text-lg font-semibold text-navy tracking-tight">
            MomentSent
          </span>
        </Link>

        {/* Quick links — always visible on desktop, hidden on mobile */}
        <div className="hidden sm:flex items-center gap-1 flex-1">
          <Link
            to="/dashboard"
            className="font-body text-sm font-medium text-gray-600 hover:text-navy hover:bg-gray-100 px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap"
          >
            Dashboard
          </Link>
          <Link
            to="/calendar"
            className="font-body text-sm font-medium text-gray-600 hover:text-navy hover:bg-gray-100 px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap"
          >
            Calendar
          </Link>
          <Link
            to="/designs"
            className="font-body text-sm font-medium text-gray-600 hover:text-navy hover:bg-gray-100 px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap"
          >
            Card Designs
          </Link>
          <Link
            to="/pricing"
            className="font-body text-sm font-medium text-gray-600 hover:text-navy hover:bg-gray-100 px-3 py-1.5 rounded-lg transition-colors"
          >
            Plans
          </Link>
        </div>

        {/* Spacer on mobile */}
        <div className="flex-1 sm:hidden" />

        {/* Right side */}
        <div className="flex items-center gap-3">

          {/* Avatar button */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen(!menuOpen)}
              className="flex items-center gap-2 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-sage"
            >
              <div className="h-9 w-9 rounded-full bg-navy flex items-center justify-center">
                <span className="font-body text-xs font-semibold text-white">{initials}</span>
              </div>
              <span className="hidden sm:block font-body text-sm text-navy">{displayName}</span>
              <svg
                className={`hidden sm:block h-4 w-4 text-gray-400 transition-transform ${menuOpen ? 'rotate-180' : ''}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Dropdown */}
            {menuOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setMenuOpen(false)}
                />
                <div className="absolute right-0 top-full mt-2 z-20 w-52 rounded-[16px] bg-white shadow-warm-lg border border-gray-100 py-2 overflow-hidden">
                  <div className="px-4 py-2 border-b border-gray-50">
                    <p className="font-body text-xs text-gray-400 truncate">{user?.email}</p>
                    {profile?.subscription_status && (
                      <p className="font-body text-xs font-medium text-sage capitalize mt-0.5">
                        {profile.subscription_status === 'trialing' ? '🕐 Free trial' :
                         profile.subscription_status === 'active'   ? '✓ Active' :
                         profile.subscription_status === 'past_due' ? '⚠ Past due' :
                         profile.subscription_status}
                      </p>
                    )}
                  </div>

                  <Link
                    to="/calendar"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm font-body text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Calendar
                  </Link>

                  <Link
                    to="/designs"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm font-body text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <span className="h-4 w-4 flex items-center justify-center text-base leading-none">🎨</span>
                    Card designs
                  </Link>

                  {hasContacts && (
                    <Link
                      to="/wrapped"
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm font-body text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <span className="h-4 w-4 flex items-center justify-center text-base leading-none">🎴</span>
                      {new Date().getFullYear()} Wrapped
                    </Link>
                  )}

                  <Link
                    to="/pricing"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm font-body text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    Plans
                  </Link>

                  <Link
                    to="/account"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm font-body text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Account settings
                  </Link>

                  <Link
                    to="/contact"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm font-body text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Contact us
                  </Link>

                  <div className="border-t border-gray-50 my-1" />

                  <button
                    type="button"
                    onClick={handleSignOut}
                    className="flex w-full items-center gap-2 px-4 py-2.5 text-sm font-body text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Sign out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>

    {/* ── Mobile bottom tab bar ──────────────────────────────────────────── */}
    <nav className="sm:hidden fixed bottom-0 left-0 right-0 z-40"
         style={{ background: '#3D6B52', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      <div className="grid grid-cols-4 h-16">
        {[
          {
            to: '/dashboard',
            label: 'Home',
            active: pathname === '/dashboard',
            icon: (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            ),
          },
          {
            to: '/designs',
            label: 'Designs',
            active: pathname === '/designs',
            icon: (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            ),
          },
          {
            to: '/calendar',
            label: 'Calendar',
            active: pathname === '/calendar',
            icon: (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            ),
          },
          {
            to: '/account',
            label: 'Account',
            active: pathname === '/account',
            icon: (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            ),
          },
        ].map((tab) => (
          <Link
            key={tab.to}
            to={tab.to}
            className={`flex flex-col items-center justify-center gap-1 text-xs font-body font-medium transition-colors ${
              tab.active ? 'text-white' : 'text-white/50'
            }`}
          >
            <span>{tab.icon}</span>
            {tab.label}
          </Link>
        ))}
      </div>
    </nav>
  </>
  );
}
